import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { CategoryQuery } from '@examdojo/category/v2';
import { RootUrlParts } from '../../app.model';
import { TOPIC_LEVEL_1_SLUG_PARAM, TOPIC_LEVEL_2_SLUG_PARAM } from './learn-hub.routes';

/**
 * Redirects `/learn-hub/:tl1Slug/:tl2Slug` → `/learn-hub/:tl1Slug/:tl2Slug/:firstPageSlug`
 * when the topic has pages. Topics with 0 pages pass through to show the empty state.
 *
 * Uses a synchronous store read — `topicsTree` is always available because
 * the parent shell route's `topicsTreeResolver` loads it before any child route activates.
 */
export const topicLevel2PageRedirectGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const categoryQuery = inject(CategoryQuery);

  const tl1Slug = route.paramMap.get(TOPIC_LEVEL_1_SLUG_PARAM);
  const tl2Slug = route.paramMap.get(TOPIC_LEVEL_2_SLUG_PARAM);

  if (!tl1Slug || !tl2Slug) {
    return true;
  }

  const topicsTree = categoryQuery.getValue().topicsTree;

  const pages = topicsTree
    ?.flatMap((syllabus) => syllabus.topics_level_01 ?? [])
    .flatMap((tl1) => tl1.topics_level_02)
    .find((tl2) => tl2.slug === tl2Slug)?.pages;

  const firstPageSlug = pages
    ?.toSorted((a, b) => a.position - b.position)
    .at(0)?.slug;

  if (!firstPageSlug) {
    return true;
  }

  return router.createUrlTree([RootUrlParts.LearnHub, tl1Slug, tl2Slug, firstPageSlug]);
};
